import { IBankAccount, updateTask, useBankAccount } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import {
  Country,
  ServiceSubcategory,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useEffect, useState } from 'react';
import { AddBankAccountModal } from '../../../bankAccount/AddBankAccountModal';
import { BankAccountDetailsModal } from '../../../misc/BankAccountDetailsModal';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import {
  ServiceRequestTaskCardConfig,
  TCommonTaskProp,
  TLinkBankAccountsTaskMetadata,
} from '../../types';
import { ServiceRequestTaskLabel } from '../labels';
import { ServiceRequestTaskIconConfig } from '../taskIconConfig';

export const AddBankAccountTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const { data: bankAccounts, mutate: mutateBankAccount } = useBankAccount({
    corporationId: serviceRequest.corporationId,
  });

  const bankAccountDetailsModalVisibility = useToggle();
  const addBankAccountModalVisibility = useToggle();

  const [bankAccountDetails, setBankAccountDetails] =
    useState<IBankAccount | null>(null);

  const taskMetadata = task?.metadata as TLinkBankAccountsTaskMetadata;

  // Disable Plaid for Indian incorporation forms
  const isIndianIncorporation = serviceRequest.commendaService
    ? serviceRequest.commendaService.country === Country.IN &&
      serviceRequest.commendaService.subcategory ===
        ServiceSubcategory.INCORPORATION
    : ['EM_INC_IN_PVTLTD', 'EM_INC_IN_LLP'].includes(
        getServiceRequestServiceId(serviceRequest) ?? ''
      );
  const disablePlaid = isIndianIncorporation;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Client adds bank account" />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex gap-2 items-center">
                <Button
                  size="sm"
                  onClick={addBankAccountModalVisibility.show}
                  variant="primary"
                >
                  Add again
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={bankAccountDetailsModalVisibility.show}
                >
                  View
                  <Icons.ArrowNarrowRight className="size-4" />
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Bank Account Added"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={addBankAccountModalVisibility.show}
                variant="primary"
              >
                Start
                <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Add your bank account" />,
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Start
                <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Add Bank Account" />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={bankAccountDetailsModalVisibility.show}
              >
                View
                <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Bank Account Added"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  useEffect(() => {
    if (bankAccounts && bankAccounts?.accounts?.length > 0) {
      const bankAccount = bankAccounts.accounts.find((account) =>
        taskMetadata?.bankAccounts?.includes(account.id)
      );
      if (bankAccount) setBankAccountDetails(bankAccount);
      else setBankAccountDetails(bankAccounts.accounts[0]);
    }
  }, [bankAccounts, serviceRequest]);

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <AddBankAccountModal
        corporationId={serviceRequest.corporationId}
        isShown={addBankAccountModalVisibility.visible}
        onClose={() => {
          addBankAccountModalVisibility.hide();
        }}
        onSaveBankAccount={async ({ bankAccountsIds }) => {
          await updateTask({
            id: task.id,
            data: {
              ...(task.status === TaskStatus.COMPLETED
                ? {}
                : { status: TaskStatus.COMPLETED }),
              metadata: { bankAccounts: bankAccountsIds },
            },
          });
          await mutate();
          await mutateBankAccount();
          addBankAccountModalVisibility.hide();
        }}
        disablePlaid={disablePlaid}
      />
      <BankAccountDetailsModal
        isShown={bankAccountDetailsModalVisibility.visible}
        setIsShown={() => bankAccountDetailsModalVisibility.hide()}
        bankAccountDetails={bankAccountDetails}
      />
    </div>
  );
};
